import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  TextField,
  ListSubheader,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CountryCode } from 'libphonenumber-js';
import { useTranslate } from '@refinedev/core';
import { CountryData, getAllCountries, PRIORITY_COUNTRY_COUNT } from './country-data';

interface CountryAutocompleteProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Normalize text for search by removing accents and converting to lowercase
 */
const normalizeForSearch = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Check if a country matches the search query
 */
const matchesSearch = (country: CountryData, query: string): boolean => {
  if (!query) return true;
  const normalizedQuery = normalizeForSearch(query);
  return country.searchTerms.includes(normalizedQuery) ||
         normalizeForSearch(country.name).includes(normalizedQuery);
};

export const CountryAutocomplete: React.FC<CountryAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  size = 'medium',
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const countries = useMemo(() => getAllCountries(), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input when menu opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the menu is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.code === value) || countries[0],
    [countries, value]
  );

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    return countries.filter((country) => matchesSearch(country, searchQuery));
  }, [countries, searchQuery]);

  // Reset search when menu closes
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <Select
      value={value}
      onChange={(e) => {
        const newValue = e.target.value as CountryCode;
        if (newValue) {
          onChange(newValue);
        }
      }}
      onOpen={handleOpen}
      onClose={handleClose}
      open={isOpen}
      disabled={disabled}
      size={size}
      variant="standard"
      sx={{
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pr: 3,
        },
        '&:before': { display: 'none' },
        '&:after': { display: 'none' },
      }}
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 400, minWidth: 320 },
        },
        // Keep menu open when clicking on search field
        autoFocus: false,
      }}
      renderValue={() => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ fontSize: '1.2em' }}>{selectedCountry.flag}</span>
          <span>{selectedCountry.dialCode}</span>
        </Box>
      )}
    >
      {/* Search field at the top */}
      <ListSubheader sx={{ bgcolor: 'background.paper', pt: 1, pb: 1 }}>
        <TextField
          size="small"
          inputRef={searchInputRef}
          placeholder={translate('phone.searchCountry')}
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            // Prevent menu from closing on key press
            e.stopPropagation();
            // Select first result on Enter
            if (e.key === 'Enter' && filteredCountries.length > 0) {
              onChange(filteredCountries[0].code);
              handleClose();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </ListSubheader>

      {/* Priority countries (France) */}
      {filteredCountries.slice(0, PRIORITY_COUNTRY_COUNT).map((country, index) => (
        <MenuItem
          key={country.code}
          value={country.code}
          sx={index === 0 && searchQuery ? { bgcolor: 'action.hover' } : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <span style={{ fontSize: '1.4em', minWidth: '1.5em' }}>{country.flag}</span>
            <Typography
              variant="body2"
              sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {country.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: alpha(theme.palette.text.primary, 0.6), fontWeight: 500, flexShrink: 0 }}
            >
              {country.dialCode}
            </Typography>
          </Box>
        </MenuItem>
      ))}

      {/* Divider after priority countries (only if France is visible) */}
      {filteredCountries.length > 0 &&
       filteredCountries[0]?.code === 'FR' &&
       filteredCountries.length > PRIORITY_COUNTRY_COUNT && (
        <Divider sx={{ my: 0.5 }} />
      )}

      {/* Other countries */}
      {filteredCountries.slice(PRIORITY_COUNTRY_COUNT).map((country, index) => {
        // Highlight first result when searching (if France is not in results)
        const isFirstResult = searchQuery && index === 0 && filteredCountries.slice(0, PRIORITY_COUNTRY_COUNT).length === 0;
        return (
          <MenuItem
            key={country.code}
            value={country.code}
            sx={isFirstResult ? { bgcolor: 'action.hover' } : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <span style={{ fontSize: '1.4em', minWidth: '1.5em' }}>{country.flag}</span>
              <Typography
                variant="body2"
                sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {country.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: alpha(theme.palette.text.primary, 0.6), fontWeight: 500, flexShrink: 0 }}
              >
                {country.dialCode}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}

      {/* No results message */}
      {filteredCountries.length === 0 && (
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {translate('phone.noCountryFound')}
          </Typography>
        </MenuItem>
      )}
    </Select>
  );
};
